import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  Avatar,
  Tooltip,
  LinearProgress,
  Badge
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as StartIcon,
  CheckCircle as CompleteIcon,
  Schedule as ScheduleIcon,
  People as PeopleIcon,
  SentimentSatisfied as SentimentIcon,
  Assignment as ActionIcon,
  Download as ExportIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/api';

interface RetrospectiveTemplate {
  id: string;
  name: string;
  description: string;
  categories: string[];
}

interface RetrospectiveResponse {
  id: string;
  text: string;
  authorId: string;
  authorName: string;
  votes: number;
  createdAt: string;
}

interface Retrospective {
  id: string;
  title: string;
  description?: string;
  template: string;
  status: 'draft' | 'in_progress' | 'completed' | 'cancelled';
  sprintId: string;
  facilitatorId: string;
  responses?: { [categoryId: string]: RetrospectiveResponse[] };
  summary?: {
    keyInsights: string[];
    actionItemsCount: number;
    sentimentScore: number;
    participationRate: number;
    completedAt?: string;
  };
  scheduledAt?: string;
  completedAt?: string;
  durationMinutes: number;
  settings: {
    allowAnonymous: boolean;
    allowVoting: boolean;
    maxVotesPerPerson: number;
    requireAllParticipants: boolean;
    autoCompleteAfterHours: number;
  };
  facilitator: {
    id: string;
    firstName: string;
    lastName: string;
  };
  actionItems?: ActionItem[];
  sentiments?: Sentiment[];
}

interface ActionItem {
  id: string;
  title: string;
  description?: string;
  status: 'open' | 'in_progress' | 'completed' | 'cancelled';
  priority: 'low' | 'medium' | 'high' | 'critical';
  dueDate?: string;
  completedAt?: string;
  completionNotes?: string;
  tags: string[];
  orderIndex: number;
  assignedTo?: {
    id: string;
    firstName: string;
    lastName: string;
  };
  createdBy: {
    id: string;
    firstName: string;
    lastName: string;
  };
}

interface Sentiment {
  id: string;
  type: 'mood' | 'confidence' | 'satisfaction' | 'stress' | 'energy';
  value: number;
  comment?: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
  };
}

interface SprintRetrospectiveProps {
  sprintId: string;
  onRetrospectiveComplete?: (retrospective: Retrospective) => void;
}

const SprintRetrospective: React.FC<SprintRetrospectiveProps> = ({
  sprintId,
  onRetrospectiveComplete
}) => {
  const { user } = useAuth();
  const [retrospectives, setRetrospectives] = useState<Retrospective[]>([]);
  const [templates, setTemplates] = useState<RetrospectiveTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  
  // Modal states
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [openViewModal, setOpenViewModal] = useState(false);
  const [selectedRetrospective, setSelectedRetrospective] = useState<Retrospective | null>(null);
  
  // Form states
  const [createForm, setCreateForm] = useState({
    title: '',
    description: '',
    template: 'start_stop_continue',
    scheduledAt: ''
  });

  const loadRetrospectives = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.get<Retrospective[]>(`/retrospectives/sprint/${sprintId}`);
      if (response.success && response.data) {
        setRetrospectives(response.data);
      }
    } catch (err: any) {
      console.error('Error loading retrospectives:', err);
      setError(err.response?.data?.message || 'Failed to load retrospectives');
    } finally {
      setLoading(false);
    }
  }, [sprintId]);

  const loadTemplates = useCallback(async () => {
    try {
      const response = await apiService.get<RetrospectiveTemplate[]>('/retrospectives/templates');
      if (response.success && response.data) {
        setTemplates(response.data);
      }
    } catch (err: any) {
      console.error('Error loading templates:', err);
    }
  }, []);

  useEffect(() => {
    loadRetrospectives();
    loadTemplates();
  }, [loadRetrospectives, loadTemplates]);

  const handleCreateRetrospective = async () => {
    try {
      const data = {
        ...createForm,
        sprintId,
        scheduledAt: createForm.scheduledAt ? new Date(createForm.scheduledAt).toISOString() : undefined
      };

      const response = await apiService.post<Retrospective>('/retrospectives', data);
      if (response.success && response.data) {
        setRetrospectives(prev => [response.data!, ...prev]);
        setOpenCreateModal(false);
        setCreateForm({ title: '', description: '', template: 'start_stop_continue', scheduledAt: '' });
      }
    } catch (err: any) {
      console.error('Error creating retrospective:', err);
      setError(err.response?.data?.message || 'Failed to create retrospective');
    }
  };

  const handleStartRetrospective = async (retrospectiveId: string) => {
    try {
      const response = await apiService.post(`/retrospectives/${retrospectiveId}/start`);
      if (response.success) {
        await loadRetrospectives();
      }
    } catch (err: any) {
      console.error('Error starting retrospective:', err);
      setError(err.response?.data?.message || 'Failed to start retrospective');
    }
  };

  const handleCompleteRetrospective = async (retrospectiveId: string) => {
    try {
      const response = await apiService.post(`/retrospectives/${retrospectiveId}/complete`);
      if (response.success) {
        await loadRetrospectives();
        const updatedRetrospective = retrospectives.find(r => r.id === retrospectiveId);
        if (updatedRetrospective && onRetrospectiveComplete) {
          onRetrospectiveComplete(updatedRetrospective);
        }
      }
    } catch (err: any) {
      console.error('Error completing retrospective:', err);
      setError(err.response?.data?.message || 'Failed to complete retrospective');
    }
  };

  const handleViewRetrospective = async (retrospective: Retrospective) => {
    try {
      const response = await apiService.get<Retrospective>(`/retrospectives/${retrospective.id}`);
      if (response.success && response.data) {
        setSelectedRetrospective(response.data);
        setOpenViewModal(true);
      }
    } catch (err: any) {
      console.error('Error loading retrospective details:', err);
      setError(err.response?.data?.message || 'Failed to load retrospective details');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft': return 'default';
      case 'in_progress': return 'primary';
      case 'completed': return 'success';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'draft': return <EditIcon />;
      case 'in_progress': return <StartIcon />;
      case 'completed': return <CompleteIcon />;
      case 'cancelled': return <CloseIcon />;
      default: return <EditIcon />;
    }
  };

  const getTemplateInfo = (templateId: string) => {
    return templates.find(t => t.id === templateId) || {
      id: templateId,
      name: 'Custom Template',
      description: 'Custom retrospective template',
      categories: []
    };
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" component="h2">
          Sprint Retrospectives
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenCreateModal(true)}
        >
          Create Retrospective
        </Button>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Retrospectives List */}
      {retrospectives.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No retrospectives yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Create your first retrospective to start gathering team feedback and insights.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenCreateModal(true)}
            >
              Create First Retrospective
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {retrospectives.map((retrospective) => {
            const templateInfo = getTemplateInfo(retrospective.template);
            return (
              <Grid item xs={12} md={6} lg={4} key={retrospective.id}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                      <Typography variant="h6" component="h3" noWrap>
                        {retrospective.title}
                      </Typography>
                      <Chip
                        icon={getStatusIcon(retrospective.status)}
                        label={retrospective.status.replace('_', ' ')}
                        color={getStatusColor(retrospective.status) as any}
                        size="small"
                      />
                    </Box>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {retrospective.description || 'No description provided'}
                    </Typography>

                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                      <Chip
                        label={templateInfo.name}
                        size="small"
                        variant="outlined"
                      />
                      {retrospective.scheduledAt && (
                        <Chip
                          icon={<ScheduleIcon />}
                          label={new Date(retrospective.scheduledAt).toLocaleDateString()}
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>

                    {/* Summary Stats */}
                    {retrospective.summary && (
                      <Box mb={2}>
                        <Grid container spacing={1}>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">
                              Action Items
                            </Typography>
                            <Typography variant="body2" fontWeight="bold">
                              {retrospective.summary.actionItemsCount}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">
                              Participation
                            </Typography>
                            <Typography variant="body2" fontWeight="bold">
                              {Math.round(retrospective.summary.participationRate)}%
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    )}

                    {/* Facilitator */}
                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                      <Avatar sx={{ width: 24, height: 24 }}>
                        {retrospective.facilitator.firstName[0]}{retrospective.facilitator.lastName[0]}
                      </Avatar>
                      <Typography variant="caption" color="text.secondary">
                        Facilitated by {retrospective.facilitator.firstName} {retrospective.facilitator.lastName}
                      </Typography>
                    </Box>
                  </CardContent>

                  <Box sx={{ p: 2, pt: 0 }}>
                    <Box display="flex" gap={1}>
                      <Button
                        size="small"
                        startIcon={<ViewIcon />}
                        onClick={() => handleViewRetrospective(retrospective)}
                      >
                        View
                      </Button>
                      {retrospective.status === 'draft' && (
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<StartIcon />}
                          onClick={() => handleStartRetrospective(retrospective.id)}
                        >
                          Start
                        </Button>
                      )}
                      {retrospective.status === 'in_progress' && (
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          startIcon={<CompleteIcon />}
                          onClick={() => handleCompleteRetrospective(retrospective.id)}
                        >
                          Complete
                        </Button>
                      )}
                    </Box>
                  </Box>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Create Retrospective Modal */}
      <Dialog open={openCreateModal} onClose={() => setOpenCreateModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Retrospective</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Title"
              value={createForm.title}
              onChange={(e) => setCreateForm(prev => ({ ...prev, title: e.target.value }))}
              required
            />
            <TextField
              fullWidth
              label="Description"
              multiline
              rows={3}
              value={createForm.description}
              onChange={(e) => setCreateForm(prev => ({ ...prev, description: e.target.value }))}
            />
            <FormControl fullWidth>
              <InputLabel>Template</InputLabel>
              <Select
                value={createForm.template}
                label="Template"
                onChange={(e) => setCreateForm(prev => ({ ...prev, template: e.target.value }))}
              >
                {templates.map((template) => (
                  <MenuItem key={template.id} value={template.id}>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {template.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {template.description}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Scheduled Date & Time"
              type="datetime-local"
              value={createForm.scheduledAt}
              onChange={(e) => setCreateForm(prev => ({ ...prev, scheduledAt: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreateModal(false)}>Cancel</Button>
          <Button
            onClick={handleCreateRetrospective}
            variant="contained"
            disabled={!createForm.title}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Retrospective Modal */}
      <Dialog open={openViewModal} onClose={() => setOpenViewModal(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedRetrospective?.title}
          <IconButton
            aria-label="close"
            onClick={() => setOpenViewModal(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedRetrospective && (
            <Box>
              {/* Retrospective Details */}
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                {selectedRetrospective.description}
              </Typography>

              {/* Status and Stats */}
              <Box display="flex" gap={2} mb={3}>
                <Chip
                  icon={getStatusIcon(selectedRetrospective.status)}
                  label={selectedRetrospective.status.replace('_', ' ')}
                  color={getStatusColor(selectedRetrospective.status) as any}
                />
                {selectedRetrospective.summary && (
                  <>
                    <Chip
                      icon={<ActionIcon />}
                      label={`${selectedRetrospective.summary.actionItemsCount} Action Items`}
                      variant="outlined"
                    />
                    <Chip
                      icon={<PeopleIcon />}
                      label={`${Math.round(selectedRetrospective.summary.participationRate)}% Participation`}
                      variant="outlined"
                    />
                    <Chip
                      icon={<SentimentIcon />}
                      label={`Sentiment: ${selectedRetrospective.summary.sentimentScore.toFixed(1)}/5`}
                      variant="outlined"
                    />
                  </>
                )}
              </Box>

              {/* Key Insights */}
              {selectedRetrospective.summary?.keyInsights && selectedRetrospective.summary.keyInsights.length > 0 && (
                <Box mb={3}>
                  <Typography variant="h6" gutterBottom>
                    Key Insights
                  </Typography>
                  <List>
                    {selectedRetrospective.summary.keyInsights.map((insight, index) => (
                      <ListItem key={index}>
                        <ListItemText primary={insight} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {/* Action Items */}
              {selectedRetrospective.actionItems && selectedRetrospective.actionItems.length > 0 && (
                <Box mb={3}>
                  <Typography variant="h6" gutterBottom>
                    Action Items ({selectedRetrospective.actionItems.length})
                  </Typography>
                  <List>
                    {selectedRetrospective.actionItems.map((actionItem) => (
                      <ListItem key={actionItem.id}>
                        <ListItemText
                          primary={actionItem.title}
                          secondary={
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                {actionItem.description}
                              </Typography>
                              <Box display="flex" gap={1} mt={1}>
                                <Chip
                                  label={actionItem.status}
                                  size="small"
                                  color={actionItem.status === 'completed' ? 'success' : 'default'}
                                />
                                <Chip
                                  label={actionItem.priority}
                                  size="small"
                                  variant="outlined"
                                />
                                {actionItem.assignedTo && (
                                  <Chip
                                    label={`Assigned to ${actionItem.assignedTo.firstName} ${actionItem.assignedTo.lastName}`}
                                    size="small"
                                    variant="outlined"
                                  />
                                )}
                              </Box>
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {/* Export Options */}
              <Box display="flex" gap={2} mt={3}>
                <Button
                  variant="outlined"
                  startIcon={<ExportIcon />}
                  disabled={selectedRetrospective.status !== 'completed'}
                >
                  Export to PDF
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={() => handleViewRetrospective(selectedRetrospective)}
                >
                  Refresh
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenViewModal(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SprintRetrospective;
